import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button, Switch, SearchBox } from '@fluentui/react-components';
import { bodyContentContainer, searchBoxStyles } from '../../app/common/styles/CommonStyleObjects';
import ScreenReaderAnnounce from '../common/ScreenReaderAnnounce';
import { StatUser, GetUsersWithPaginationQuery } from '../../utils/statApi/UsersApi';
import PageHeaderWithActionButtons from '../common/headers/PageHeaderWithActionButtons';
import Subheader from '../common/headers/Subheader';
import UsersList from './UsersList';
import UserDetailsPanel from './UserDetailsPanel';
import FullWidthHeader from '../common/headers/FullWidthHeader';
import CanAccess from '../common/canAccess/CanAccess';
import WrappedListPage, {
  IPaginatedListPageProps,
} from '../common/DetailsLists/pagination/PaginatedListPage';

interface IUserListPageState {
  isInactive: boolean;
  isInvalid: boolean;
}

const UsersListPage = (): JSX.Element => {
  const [isInputError, setIsInputError] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>(null);
  const [statUsers, setStatUsers] = useState<Array<StatUser>>([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterState, setFilterState] = useState<IUserListPageState>({
    isInactive: false,
    isInvalid: false,
  });

  const isValidSearchTerm = (value: string): boolean => value && value.length > 2;

  const onUserAddClick = (): void => {
    setSelectedUser(null);
    setIsPanelOpen(true);
  };

  const onUserEditClick = (item: StatUser): void => {
    setSelectedUser(item);
    setIsPanelOpen(true);
  };

  const onPanelDismiss = (): void => {
    setSelectedUser(null);
    setIsPanelOpen(false);
  };

  const handleOnChangeSearch = (
    event: React.ChangeEvent<HTMLInputElement>,
    data: { value: string },
  ): void => {
    setIsInputError(false);
    const searchText = data.value ? data.value.trim() : '';

    if (isValidSearchTerm(searchText)) {
      setSearchTerm(searchText);
    } else if (searchText.length === 0) {
      setSearchTerm(null);
    }
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      const inputValue = event.currentTarget.value;
      if (!isValidSearchTerm(inputValue)) setIsInputError(true);
    }
  };

  const renderHeaderButtons = (): JSX.Element => {
    return (
      <CanAccess requestedAction="GlobalUser:Add">
        <div data-testid="add-user">
          <Button data-testid="add-user-button" appearance="primary" onClick={onUserAddClick}>
            Add New User
          </Button>
        </div>
      </CanAccess>
    );
  };

  const renderHeader = (): JSX.Element => (
    <PageHeaderWithActionButtons title="Security" actionButtons={renderHeaderButtons()} />
  );

  const handleIsInactiveToggleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    data: { checked: boolean },
  ) => {
    setFilterState({
      isInactive: data.checked,
      isInvalid: filterState.isInvalid,
    });
  };

  const handleIsInvalidToggleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    data: { checked: boolean },
  ) => {
    setFilterState({
      isInactive: filterState.isInactive,
      isInvalid: data.checked,
    });
  };

  const horizontalGapStackTokens = {
    childrenGap: 10,
    padding: 10,
  };

  const renderSubtitle = (): JSX.Element => (
    <div data-testid="users-list">
      <Subheader title="User Management">
        <Stack
          horizontalAlign="space-between"
          horizontal
          wrap
          verticalAlign="center"
          tokens={horizontalGapStackTokens}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
            <Switch label="Show Deactivated Users" onChange={handleIsInactiveToggleChange} />
            <Switch label="Show Invalid Users" onChange={handleIsInvalidToggleChange} />
          </Stack>
          <Stack horizontal verticalAlign="center" grow>
            <SearchBox
              aria-label="search users"
              placeholder="Search users"
              onChange={handleOnChangeSearch}
              onKeyDown={handleOnKeyDown}
              style={searchBoxStyles}
            />
          </Stack>
        </Stack>
      </Subheader>
      {searchTerm && (
        <ScreenReaderAnnounce
          message={`${statUsers?.length} user${statUsers?.length === 1 ? '' : 's'} found`}
        />
      )}
    </div>
  );

  const wrappedListPageParams: IPaginatedListPageProps<StatUser> = {
    dataMapperKey: 'statUsers',
    actionName: 'loading the user list data',
    query: GetUsersWithPaginationQuery,
    searchTerm,
    isInputError,
    setIsInputError,
    setData: setStatUsers,
    onRowClick: onUserEditClick,
    filters: filterState,
  };

  return (
    <>
      <FullWidthHeader title={renderHeader} subtitle={renderSubtitle} />
      <div className={`${bodyContentContainer}  ms-depth-4`}>
        {WrappedListPage(UsersList, wrappedListPageParams)}
      </div>
      {/* Begin Add New User Panel */}
      {isPanelOpen && <UserDetailsPanel selectedUser={selectedUser} onDismiss={onPanelDismiss} />}
    </>
  );
};

export default UsersListPage;
